import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { useMe } from '@entities/user/api';
import { Button, Container, Skeleton, StateBlock } from '@shared/ui';

import { useAuthDialog } from '../model/authDialogContext';
import type { PendingActionType } from '../model/pendingAction';

import styles from './RequireAuth.module.css';

interface RequireAuthProps {
  children: ReactNode;
  /** Что выполнить после входа. По умолчанию — просто вернуться на этот адрес. */
  pendingType?: PendingActionType | undefined;
}

/**
 * Guard закрытого экрана (MVP_TECH_RFC §22).
 *
 * Проверка идёт через `GET /auth/me`, а не по локальному флагу: клиент не
 * хранит токен и знать о сессии ничего не может. **Гард не является защитой** —
 * ownership и авторизацию проверяет backend на каждом запросе; здесь решается
 * только то, что показать вместо экрана.
 *
 * Окно входа открывается автоматически один раз: пользователь пришёл сюда за
 * подбором, и лишний клик по «Войти» ничего не добавляет. После успеха
 * `AuthProvider` вернёт его на сохранённый адрес и выполнит отложенное действие.
 */
export function RequireAuth({ children, pendingType = 'navigate' }: RequireAuthProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { requestAuth } = useAuthDialog();
  const { data: session, isPending, isError, refetch } = useMe();
  const prompted = useRef(false);

  const anonymous = !isPending && !isError && session === null;
  const returnUrl = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!anonymous || prompted.current) return;
    prompted.current = true;
    requestAuth({ type: pendingType, returnUrl });
  }, [anonymous, pendingType, requestAuth, returnUrl]);

  if (isPending) {
    return (
      <Container>
        <div className={styles.loading}>
          <Skeleton height="2rem" width="40%" />
          <Skeleton height="12rem" radius="var(--radius-card)" />
        </div>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <div className={styles.state}>
          <StateBlock
            tone="error"
            title={t('error.title')}
            description={t('error.description')}
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  void refetch();
                }}
              >
                {t('error.retry')}
              </Button>
            }
          />
        </div>
      </Container>
    );
  }

  if (session === null) {
    return (
      <Container>
        <div className={styles.state}>
          <StateBlock
            title={t('auth.guard.title')}
            description={t('auth.guard.text')}
            action={
              <Button
                onClick={() => {
                  requestAuth({ type: pendingType, returnUrl });
                }}
              >
                {t('nav.login')}
              </Button>
            }
          />
        </div>
      </Container>
    );
  }

  return <>{children}</>;
}
